import { AxiosError, AxiosResponse } from 'axios';
import { left, right } from '../../../../shared/core/Either';
import { Result } from '../../../../shared/core/Result';
import { APIResponse } from '../../../../shared/infra/api/APIResponse';
import { BaseAPI } from '../../../../shared/infra/api/BaseAPI';
import { IAuthService } from '../../../users/services/authService';
import { AnalyticCountResponseDTO } from '../dtos/AnalyticCount.dto';
import { LogDateCountResponseDTO } from '../dtos/LogDateCount.dto';
import { TopContentResponseDTO } from '../dtos/TopContent.dto';
import { AnalyticCountModel } from '../models/AnalyticCount.model';
import { LogDateCountModel } from '../models/LogDateCount.model';
import { TopContentModel } from '../models/TopContent.model';
import {
  ExtendedAnalyticTypes,
  InsightsRequest,
  SystemInsightResponseDTO,
  UserInsightResponseDTO,
} from '../types/AnalyticsTypes';
import { AnalyticCountUtil } from '../utils/AnalyticCountUtil';
import { LogDateCountUtil } from '../utils/LogDateCountUtil';
import { TopContentUtil } from '../utils/TopContentUtil';
import { SystemInsightsModel } from '../models/SystemInsights.model';
import { UserInsightsModel } from '../models/UserInsights.model';

export interface IAnalyticsService {
  getAnalyticsCount(
    type: ExtendedAnalyticTypes,
  ): Promise<APIResponse<AnalyticCountModel>>;
  getLogDateCount(
    type: ExtendedAnalyticTypes,
    filter?: string,
    range?: Date[],
  ): Promise<APIResponse<LogDateCountModel[]>>;
  getTopContent(): Promise<APIResponse<TopContentModel[]>>;
  getSystemInsights(
    insightRequest: InsightsRequest,
  ): Promise<APIResponse<SystemInsightsModel>>;
  getUserInsights(
    insightRequest: InsightsRequest,
  ): Promise<APIResponse<UserInsightsModel>>;
}

export class AnalyticsService extends BaseAPI implements IAnalyticsService {
  protected onFailureExecuteImpl(error: AxiosError<any>): Promise<any> {
    throw new Error(`Method not implemented. ${error.name}`);
  }

  constructor(authService: IAuthService) {
    super(authService);
  }

  hasToken(): boolean {
    return this.authService.isUserAuthenticated();
  }

  public async getTopContent(
    filter?: string,
    postType?: string,
    range?: Date[],
  ): Promise<APIResponse<TopContentModel[]>> {
    try {
      const response = await this.get<TopContentResponseDTO>({
        url: '/v1/admin/analytics/top-content',
        requiresToken: true,
        config: {
          params: {
            filter,
            postType,
            range,
          },
        },
      });

      const { data } = response.data;

      return right(
        Result.ok<TopContentModel[]>(
          data.map((item) => TopContentUtil.mapToViewModel(item)),
        ),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async getUserInsights(
    insightRequest: InsightsRequest,
  ): Promise<APIResponse<UserInsightsModel>> {
    const { filter } = insightRequest;

    try {
      const response = await this.get<UserInsightResponseDTO>({
        url: '/v2/insights/user',
        requiresToken: true,
        config: {
          params: {
            filter,
          },
        },
      });

      const { data } = response.data;

      return right(Result.ok<UserInsightsModel>(data));
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async getSystemInsights(
    insightRequest: InsightsRequest,
  ): Promise<APIResponse<SystemInsightsModel>> {
    const { filter } = insightRequest;

    try {
      const response = await this.get<SystemInsightResponseDTO>({
        url: '/v2/insights/system',
        requiresToken: true,
        config: {
          params: {
            filter,
          },
        },
      });

      const { data } = response.data;

      return right(Result.ok<SystemInsightsModel>(data));
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }
  public async getLogDateCount(
    type: ExtendedAnalyticTypes,
    filter?: string,
    range?: Date[],
  ): Promise<APIResponse<LogDateCountModel[]>> {
    let response: AxiosResponse<LogDateCountResponseDTO>;

    try {
      switch (type) {
        case ExtendedAnalyticTypes.GUEST_CONTENT:
          response = await this.get<LogDateCountResponseDTO>({
            url: '/v2/guests/admin/log-date',
            requiresToken: true,
            config: {
              params: {
                type: 'CONTENT',
                filter,
                range,
              },
            },
          });
          break;
        case ExtendedAnalyticTypes.UPGRADE:
          response = await this.get<LogDateCountResponseDTO>({
            url: '/v2/guests/admin/log-date',
            requiresToken: true,
            config: {
              params: {
                type: 'UPGRADE',
                filter,
                range,
              },
            },
          });
          break;
        case ExtendedAnalyticTypes.GUEST_NOTIFICATION:
          response = await this.get<LogDateCountResponseDTO>({
            url: '/v2/guests/admin/log-date',
            requiresToken: true,
            config: {
              params: {
                type: 'NOTIFICATION',
                filter,
                range,
              },
            },
          });
          break;
        case ExtendedAnalyticTypes.GUEST:
          response = await this.get<LogDateCountResponseDTO>({
            url: '/v2/guests/admin/guests',
            requiresToken: true,
            config: {
              params: {
                filter,
                range,
              },
            },
          });
          break;
        default:
          response = await this.get<LogDateCountResponseDTO>({
            url: '/v1/admin/analytics/log-date',
            requiresToken: true,
            config: {
              params: {
                type,
                filter,
                range,
              },
            },
          });
          break;
      }

      const { data } = response.data;

      return right(
        Result.ok<LogDateCountModel[]>(
          data.map((item) => LogDateCountUtil.mapToViewModel(item)),
        ),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async getAnalyticsCount(
    type: ExtendedAnalyticTypes,
  ): Promise<APIResponse<AnalyticCountModel>> {
    let response: AxiosResponse<AnalyticCountResponseDTO>;
    try {
      switch (type) {
        case ExtendedAnalyticTypes.GUEST:
          response = await this.get<AnalyticCountResponseDTO>({
            url: '/v2/guests/analytics/activity',
            requiresToken: true,
          });
          break;
        case ExtendedAnalyticTypes.GUEST_CONTENT:
          response = await this.get<AnalyticCountResponseDTO>({
            url: '/v2/guests/admin/analytics',
            requiresToken: true,
            config: {
              params: { type: 'CONTENT' },
            },
          });
          break;
        case ExtendedAnalyticTypes.GUEST_NOTIFICATION:
          response = await this.get<AnalyticCountResponseDTO>({
            url: '/v2/guests/admin/analytics',
            requiresToken: true,
            config: {
              params: { type: 'NOTIFICATION' },
            },
          });
          break;
        case ExtendedAnalyticTypes.UPGRADE:
          response = await this.get<AnalyticCountResponseDTO>({
            url: '/v2/guests/admin/analytics',
            requiresToken: true,
            config: {
              params: { type: 'UPGRADE' },
            },
          });
          break;
        default:
          response = await this.get<AnalyticCountResponseDTO>({
            url: '/v1/admin/analytics',
            requiresToken: true,
            config: {
              params: { type },
            },
          });
          break;
      }

      const { data } = response.data;

      return right(
        Result.ok<AnalyticCountModel>(AnalyticCountUtil.mapToViewModel(data)),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }
}
